import { PROVIDER_PATH } from "../config";
import { LOGIN_CALLBACK_PATH, PROVIDER_LOGIN_PATH } from "../nav/config";
import { clientUserAuthGate } from "../statsig/gates";
import { Statsig } from "../statsig/StatsigProvider";
import { Env, UserType } from "../types";
import { getEnvFromHostName } from "../utils";
import { getAndSetUserTypeMemoized } from "./userType";

export type Auth0Config = {
  domain: string;
  clientId: string;
  /**
   * The correct audience for the app to use. This is determined based on the
   * user type.
   *
   * NOTE: Client users will use the monolith audience which is accepted by the
   * Telehealth backend for client-authorized requests and the monolith
   * backend. Provider users will use the Telehealth audience which is accepted
   * by the Telehealth backend and the monolith backend for provider-authorized
   * requests. Ideally, the monolith and Telehealth backends would expect the
   * same API audience. However, presently, they do not. Due to expectations
   * from the monolith about what audience to expect for client requests, we opt
   * to use the monolith audience for client requests to the telehealth backend
   * instead. The audience is set on app initialization and is used for all
   * requests made by the app. Swapping audience after the Auth0 provider has
   * been initialized is not recommended as it can lead to unexpected auth
   * issues in Safari and incognito browsers.
   */
  audience: string;
  scope: string;
  loginUri: string;
  redirectUri: string;
};

const SCOPE = "openid email profile offline_access";

function getRedirectUri() {
  const isClientUserAuthEnabled = Statsig.checkGate(clientUserAuthGate);
  return `${location.origin}${isClientUserAuthEnabled ? LOGIN_CALLBACK_PATH : PROVIDER_PATH}`;
}

export function getAuth0Config(): Auth0Config {
  const env = getEnvFromHostName(window.location.hostname);
  const userType = getAndSetUserTypeMemoized(window.location.pathname);

  switch (env) {
    case Env.PROD: {
      const MONOLITH_AUDIENCE = "https://growtherapy.us.auth0.com/api/v2/";
      const TELEHEALTH_AUDIENCE = "https://telehealth.growtherapy.com/api";

      return {
        domain: "growtherapy.us.auth0.com",
        clientId: "guGGikPlebDR5Qa1yJ578syyaCYlIOmj",
        audience:
          userType === UserType.CLIENT
            ? MONOLITH_AUDIENCE
            : TELEHEALTH_AUDIENCE,
        scope: SCOPE,
        loginUri: `${location.origin}${PROVIDER_LOGIN_PATH}`,
        redirectUri: getRedirectUri(),
      };
    }
    case Env.DEV: {
      const MONOLITH_AUDIENCE = `https://${import.meta.env.VITE_DEVELOPMENT_AUTH0_DOMAIN as string}/api/v2/`;
      const TELEHEALTH_AUDIENCE = import.meta.env
        .VITE_DEVELOPMENT_AUTH0_AUDIENCE as string;

      return {
        domain: import.meta.env.VITE_DEVELOPMENT_AUTH0_DOMAIN as string,
        clientId: import.meta.env.VITE_DEVELOPMENT_AUTH0_CLIENT_ID as string,
        audience:
          userType === UserType.CLIENT
            ? MONOLITH_AUDIENCE
            : TELEHEALTH_AUDIENCE,
        scope: SCOPE,
        loginUri: `${location.origin}${PROVIDER_LOGIN_PATH}`,
        redirectUri: getRedirectUri(),
      };
    }
    default: {
      const MONOLITH_AUDIENCE =
        "https://growtherapystaging.us.auth0.com/api/v2/";
      const TELEHEALTH_AUDIENCE =
        "https://telehealth.growtherapystaging.com/api";

      return {
        domain: "growtherapystaging.us.auth0.com",
        clientId: "HUCGLL3eECkRLo0xhu5Dp7sOygBHmNvL",
        audience:
          userType === UserType.CLIENT
            ? MONOLITH_AUDIENCE
            : TELEHEALTH_AUDIENCE,
        scope: SCOPE,
        loginUri: `${location.origin}${PROVIDER_LOGIN_PATH}`,
        redirectUri: getRedirectUri(),
      };
    }
  }
}
